import React, { useState } from 'react';
import { Building2, Check, ChevronDown, Star } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { useAuthStore, type OrgMembership } from '../stores/auth.store';
import { roleBadgeClass } from '../lib/org-constants';

/**
 * Organization switcher dropdown component.
 * Shows all user's organizations with the active one highlighted,
 * default indicator, and role badges. Triggers org switch on selection.
 */
export const OrgSwitcher: React.FC = () => {
  const { organizations, activeOrgId, switchOrg } = useAuthStore();
  const [isSwitching, setIsSwitching] = useState(false);

  const activeOrg = organizations.find((m) => m.orgId === activeOrgId);

  /**
   * Handle organization switch — calls store action which re-issues JWT
   * and reconnects WebSocket.
   */
  const handleSwitchOrg = async (orgId: string) => {
    if (orgId === activeOrgId || isSwitching) return;
    setIsSwitching(true);
    try {
      await switchOrg(orgId);
    } catch (error) {
      console.error('Failed to switch organization:', error);
    } finally {
      setIsSwitching(false);
    }
  };

  if (organizations.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-auto w-auto px-3 py-1.5 flex items-center gap-2"
          disabled={isSwitching}
          data-testid="org-switcher-trigger"
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="hidden sm:inline text-sm font-medium truncate max-w-[160px]">
            {activeOrg?.organization.name || 'Select Org'}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Organizations
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((membership) => (
          <OrgMenuItem
            key={membership.orgId}
            membership={membership}
            isActive={membership.orgId === activeOrgId}
            onSelect={handleSwitchOrg}
            disabled={isSwitching}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Individual organization menu item with role badge and default indicator.
 */
const OrgMenuItem: React.FC<{
  membership: OrgMembership;
  isActive: boolean;
  onSelect: (orgId: string) => void;
  disabled: boolean;
}> = ({ membership, isActive, onSelect, disabled }) => {
  return (
    <DropdownMenuItem
      onClick={() => onSelect(membership.orgId)}
      disabled={disabled}
      className="flex items-center justify-between py-2.5 cursor-pointer"
      data-testid={`org-item-${membership.orgId}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isActive ? (
          <Check className="h-4 w-4 text-primary shrink-0" data-testid="active-check" />
        ) : (
          <div className="h-4 w-4 shrink-0" />
        )}
        <span className="truncate text-sm">{membership.organization.name}</span>
        {membership.isDefault && (
          <Star className="h-3 w-3 text-amber-500 shrink-0" data-testid="default-star" />
        )}
      </div>
      <Badge
        variant="secondary"
        className={`text-xs shrink-0 ${roleBadgeClass[membership.role] || ''}`}
      >
        {membership.role}
      </Badge>
    </DropdownMenuItem>
  );
};
